import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid, Cell,
} from "recharts";
import { Wrench, MapPin, Stack, ChartLineUp, Gauge, TrendUp, TrendDown, DownloadSimple, Printer, UsersThree } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/date";

const ACCENT = "#FED001";
const WARM = ["#C9A227", "#8A7A3A", "#A8895B", "#6E6E6E", "#7C6F5A", "#B8842B", "#5F6B4E", "#9B7B4A"];
const money = (n) => "$" + Math.round(n).toLocaleString("en-AU");
const fmtd = (d) => fmtDate(d);
const iso = (d) => d.toISOString().slice(0, 10);

function rangeParams(preset, cs, ce) {
  const today = new Date();
  if (preset === "week") {
    const day = (today.getDay() + 6) % 7;
    const mon = new Date(today); mon.setDate(today.getDate() - day);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { start: iso(mon), end: iso(sun) };
  }
  if (preset === "month") {
    return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
  }
  if (preset === "custom" && cs && ce) return { start: cs, end: ce };
  return {};
}

const utilTone = (p) =>
  p < 15 ? "bg-[#FBE7E4] text-[#B42318] border-[#F1C4BC]"
    : p < 60 ? "bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]"
      : "bg-[#E4F2E4] text-[#256428] border-[#BFE0C0]";

function Card({ children, className = "", testid }) {
  return <div data-testid={testid} className={`bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden ${className}`}>{children}</div>;
}

function SectionTitle({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-divider-inner">
      {Icon && <Icon size={17} weight="bold" className="text-accent-ink" />}
      <h3 className="font-bold text-[15px] text-ink-primary">{title}</h3>
      {sub && <span className="text-[11px] text-ink-tertiary">{sub}</span>}
    </div>
  );
}

function Sparkline({ data }) {
  const max = Math.max(1, ...data);
  return (
    <div className="flex items-end gap-[2px] h-6" title="14-day activity">
      {data.map((v, i) => (
        <div key={i} className={`w-[3px] rounded-[1px] ${v ? "bg-[#C9A227]" : "bg-border"}`}
          style={{ height: `${Math.max(12, (v / max) * 100)}%` }} />
      ))}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <Card className="p-4" testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
        {Icon && <span className="w-7 h-7 rounded-full bg-surface-inset flex items-center justify-center"><Icon size={15} weight="bold" className={accent || "text-ink-tertiary"} /></span>}
      </div>
      <div className="text-[30px] font-extrabold text-ink-primary mt-2 leading-none">{value}</div>
      {sub && <div className="text-[11.5px] text-ink-tertiary mt-1.5">{sub}</div>}
    </Card>
  );
}

const colHead = "text-left px-3 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-colhead whitespace-nowrap";

export default function DashboardView({ projectId, projectName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("program");
  const [cs, setCs] = useState("");
  const [ce, setCe] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rp = rangeParams(preset, cs, ce);
      const params = { ...(projectId ? { project_id: projectId } : {}), ...rp };
      const { data } = await api.get("/dashboard", { params });
      setData(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, preset, cs, ce]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const k = data.kpis;
    const rows = [
      ["EQUIPR Civil — Dashboard", projectName || "All Projects"],
      ["Period", k.program_start, "to", k.program_end, `(${k.program_days} days)`],
      [],
      ["Avg Utilisation %", k.avg_utilisation],
      ["Assets On Hire", k.assets_on_hire], ["Workers", k.operators_on_hire],
      ["Est Revenue", k.est_revenue], ["Est Cost", k.est_cost], ["Est Margin", k.est_margin],
      [],
      ["Tag", "Item", "Supplier", "Zone", "Allocated Days", "Active Days", "Util %", "Idle Days", "Last Active"],
      ...data.utilisation.map((r) => [r.tag, r.item, r.supplier, r.zone, r.allocated_days, r.active_days, r.util_pct, r.idle_days, r.last_active]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `equipr-dashboard-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !data) return <div className="p-8 text-center text-ink-tertiary text-sm" data-testid="dashboard-loading">Loading dashboard…</div>;

  const k = data.kpis;
  const marginPct = k.est_revenue ? Math.round((k.est_margin / k.est_revenue) * 100) : 0;
  const presetBtn = (val, label) => (
    <button onClick={() => setPreset(val)} data-testid={`range-${val}`}
      className={`px-3 h-8 text-[12px] rounded-[999px] font-bold uppercase tracking-[0.04em] ${preset === val ? "bg-equipr-yellow text-black" : "text-ink-secondary hover:text-ink-primary"}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-4 dashboard-print" data-testid="dashboard-view">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-[18px] font-bold text-ink-primary">{projectName || "All Projects"} · Dashboard</h2>
          <span className="text-[12px] text-ink-tertiary">Period {fmtd(k.program_start)} – {fmtd(k.program_end)} · {k.program_days} days</span>
        </div>
        <div className="flex items-center gap-2 no-print">
          <div className="flex items-center bg-surface-inset rounded-[999px] p-0.5" data-testid="range-controls">
            {presetBtn("program", "Program")}
            {presetBtn("week", "This Week")}
            {presetBtn("month", "This Month")}
            {presetBtn("custom", "Custom")}
          </div>
          {preset === "custom" && (
            <div className="flex items-center gap-1">
              <input type="date" value={cs} onChange={(e) => setCs(e.target.value)} data-testid="range-custom-start"
                className="h-8 px-2 border border-border-control text-[12px] rounded-[7px]" />
              <span className="text-ink-tertiary text-xs">→</span>
              <input type="date" value={ce} onChange={(e) => setCe(e.target.value)} data-testid="range-custom-end"
                className="h-8 px-2 border border-border-control text-[12px] rounded-[7px]" />
            </div>
          )}
          <button onClick={exportCSV} data-testid="export-csv"
            className="h-8 px-3 border border-border-control text-[12px] font-semibold text-ink-primary hover:bg-surface-panel rounded-[7px] inline-flex items-center gap-1">
            <DownloadSimple size={14} weight="bold" /> CSV
          </button>
          <button onClick={() => window.print()} data-testid="export-pdf"
            className="h-8 px-3 bg-equipr-black text-white text-[12px] font-semibold hover:bg-black rounded-[7px] inline-flex items-center gap-1">
            <Printer size={14} weight="bold" /> PDF
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Avg Utilisation" value={`${k.avg_utilisation}%`} icon={Gauge} accent="text-accent-ink" sub="allocation basis" testid="kpi-avg-utilisation" />
        <Kpi label="Assets On Hire" value={k.assets_on_hire} icon={Stack} sub={`${k.projects} project${k.projects === 1 ? "" : "s"}`} testid="kpi-assets-on-hire" />
        <Kpi label="Workers" value={k.operators_on_hire} icon={UsersThree} sub={`${k.stand_downs} stand · ${k.off_hires} off hire`} testid="kpi-workers" />
        <Kpi label="Est. Margin" value={money(k.est_margin)} icon={marginPct >= 0 ? TrendUp : TrendDown} accent={marginPct >= 0 ? "text-green-600" : "text-red-600"} sub={`${marginPct}% of charge · rev ${money(k.est_revenue)}`} testid="kpi-est-margin" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="chart-by-category">
          <SectionTitle icon={Stack} title="On hire by category" />
          <div className="p-3" style={{ height: 260 }}>
            {data.on_hire_by_category.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.on_hire_by_category} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#8A857A" }} />
                  <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11, fill: "#59554D" }} />
                  <Tooltip cursor={{ fill: "#FBF6E3" }} />
                  <Bar dataKey="count" fill={ACCENT} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card testid="chart-by-zone">
          <SectionTitle icon={MapPin} title="Where on site" />
          <div className="p-3" style={{ height: 260 }}>
            {data.on_hire_by_zone.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.on_hire_by_zone} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#8A857A" }} />
                  <YAxis type="category" dataKey="zone" width={130} tick={{ fontSize: 11, fill: "#59554D" }} />
                  <Tooltip cursor={{ fill: "#F2EFE8" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {data.on_hire_by_zone.map((_, i) => <Cell key={i} fill={WARM[i % WARM.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* Plant over program */}
      <Card testid="chart-over-program">
        <SectionTitle icon={ChartLineUp} title="Plant on hire over the program" />
        <div className="p-3" style={{ height: 240 }}>
          {data.plant_over_program.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.plant_over_program}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DD" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A857A" }} tickFormatter={(d) => new Date(d).toLocaleDateString("en-AU", { month: "short", year: "2-digit" })} minTickGap={40} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8A857A" }} />
                <Tooltip labelFormatter={(d) => fmtd(d)} />
                <Area type="monotone" dataKey="count" stroke="#C9A227" strokeWidth={2} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* Forecast vs Actual */}
      {data.forecast_vs_actual.length > 0 && (
        <Card testid="chart-forecast">
          <SectionTitle icon={ChartLineUp} title="Forecast vs actual" sub="equipment needed vs allocated" />
          <div className="p-3" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.forecast_vs_actual}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DD" />
                <XAxis dataKey="asset_type" tick={{ fontSize: 11, fill: "#59554D" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8A857A" }} />
                <Tooltip cursor={{ fill: "#FBF6E3" }} />
                <Bar dataKey="needed" name="Needed" fill="#B4AEA1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="allocated" name="Allocated" fill={ACCENT} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Hours by Task / Activity */}
      {(data.task_hours || []).length > 0 && (
        <Card testid="chart-task-hours">
          <SectionTitle icon={ChartLineUp} title="Hours by task / activity" sub="from docket task bookings" />
          <div className="p-3" style={{ height: Math.max(160, 40 * data.task_hours.length + 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.task_hours.map((t) => ({ ...t, label: `${t.code ? t.code + " — " : ""}${t.name}${t.manual ? " *" : ""}` }))}
                layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: "#8A857A" }} />
                <YAxis type="category" dataKey="label" width={170} tick={{ fontSize: 11, fill: "#59554D" }} />
                <Tooltip cursor={{ fill: "#FBF6E3" }} formatter={(v) => [`${v} h`, "Hours"]} />
                <Bar dataKey="hours" fill={ACCENT} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {data.task_hours.some((t) => t.manual) && <p className="px-4 pb-2 text-[11px] text-ink-tertiary">* manually entered on the docket (not a project task)</p>}
        </Card>
      )}

      {/* Worker Compliance alerts */}
      {(data.compliance_alerts || []).length > 0 && (
        <Card testid="table-compliance">
          <SectionTitle icon={Wrench} title={`Worker compliance — expired / expiring (${data.compliance_alerts.length})`} />
          <div className="overflow-x-auto">
            <table className="w-full text-cell">
              <thead><tr className="bg-surface-sunken border-b border-border">
                {["Worker", "Type", "Item", "Ref #", "Expiry", "Status"].map((h) => (<th key={h} className={colHead}>{h}</th>))}
              </tr></thead>
              <tbody>
                {data.compliance_alerts.map((c, i) => (
                  <tr key={i} className="border-b border-divider-row" data-testid={`compliance-alert-${i}`}>
                    <td className="px-3 py-2 font-semibold text-ink-primary">{c.worker}</td>
                    <td className="px-3 py-2 text-ink-secondary">{c.ctype}</td>
                    <td className="px-3 py-2 text-ink-secondary">{c.item}</td>
                    <td className="px-3 py-2 text-ink-tertiary">{c.ref_no || "—"}</td>
                    <td className="px-3 py-2 text-ink-secondary">{fmtd(c.expiry)}</td>
                    <td className="px-3 py-2">
                      {c.level === "expired"
                        ? <span className="px-2 py-0.5 text-[11px] font-bold rounded-full border bg-[#FBE7E4] text-[#B42318] border-[#F1C4BC]">Expired {Math.abs(c.days_left)}d ago</span>
                        : <span className="px-2 py-0.5 text-[11px] font-bold rounded-full border bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]">{c.days_left}d left</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Utilisation table */}
      <Card testid="table-utilisation">
        <SectionTitle icon={Gauge} title="Asset & worker usage" sub="lowest utilisation first" />
        <div className="overflow-x-auto">
          <table className="w-full text-cell">
            <thead><tr className="bg-surface-sunken border-b border-border">
              {["Tag", "Item", "Supplier", "Zone", "Alloc. days", "Active", "Util %", "Idle", "Last active", "Trend (14d)"].map((h) => (<th key={h} className={colHead}>{h}</th>))}
            </tr></thead>
            <tbody>
              {data.utilisation.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-ink-tertiary">No allocations.</td></tr>
              ) : data.utilisation.map((r, i) => (
                <tr key={i} className="border-b border-divider-row hover:bg-accent-wash" data-testid={`util-row-${i}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-ink-primary">{r.tag}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.item || "—"}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.supplier || "—"}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.zone || "—"}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.allocated_days}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.active_days}</td>
                  <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 text-[12px] font-bold border rounded-full ${utilTone(r.util_pct)}`}>{r.util_pct}%</span></td>
                  <td className="px-3 py-2 text-ink-secondary">{r.idle_days}</td>
                  <td className="px-3 py-2 text-ink-secondary">{fmtd(r.last_active)}</td>
                  <td className="px-3 py-2"><Sparkline data={r.trend} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Service due / overdue */}
      <Card testid="table-service">
        <SectionTitle icon={Wrench} title={`Service due / overdue (${data.service_due.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-cell">
            <thead><tr className="bg-surface-sunken border-b border-border">
              {["Tag", "Item", "Location", "Due", "When"].map((h) => <th key={h} className={colHead}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.service_due.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-ink-tertiary">Nothing due — set a Service Due date on assets to track this.</td></tr>
              ) : data.service_due.map((r, i) => (
                <tr key={i} className="border-b border-divider-row hover:bg-accent-wash" data-testid={`service-row-${i}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-ink-primary">{r.tag}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.item || "—"}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.zone || "—"}</td>
                  <td className="px-3 py-2 text-ink-secondary">{r.unit === "h" ? r.due : fmtd(r.due)}</td>
                  <td className="px-3 py-2">
                    {r.days_overdue > 0 ? (
                      <span className="inline-block px-2 py-0.5 text-[12px] font-bold rounded-full border bg-[#FBE7E4] text-[#B42318] border-[#F1C4BC]">⚠ {r.days_overdue}{r.unit || "d"} overdue</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 text-[12px] font-bold rounded-full border bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]">in {Math.abs(r.days_overdue)}{r.unit || "d"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Empty() {
  return <div className="h-full flex items-center justify-center text-ink-tertiary text-sm">No data to display.</div>;
}
